import {
  memo, useState, useCallback, useMemo, useRef, useEffect,
} from 'react';
import { motion } from 'framer-motion';
import { useToastContext } from '@/hooks/useToastContext';
import { formatDateWithWeekday } from '@/utils/date';
import type { InviteData } from '@/types/invite';

// Компоненты специфичные для этого layout
import CharmingNaturalHeader from './components/CharmingNaturalHeader';
import CharmingNaturalNavigation from './components/CharmingNaturalNavigation';
import CharmingNaturalFooter from './components/CharmingNaturalFooter';
import CharmingNaturalRSVPForm from './components/CharmingNaturalRSVPForm';

// Стили только для этого layout
import styles from './CharmingNaturalLayout.module.css';

type CharmingNaturalLayoutProps = {
  invite: InviteData;
};

/**
 * Премиум layout "Charming Natural"
 *
 * Полностью самостоятельный layout со своими:
 * - Секциями (header, names, main, rsvp)
 * - Компонентами (CharmingNaturalHeader, CharmingNaturalNavigation)
 * - Логикой RSVP
 *
 * НЕ использует ThemeStyles - все стили в CharmingNaturalLayout.module.css
 */
const CharmingNaturalLayout = memo(({ invite }: CharmingNaturalLayoutProps) => {
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [showRSVPForm, setShowRSVPForm] = useState(false);
  const { showSuccess } = useToastContext();
  const namesRef = useRef<HTMLHeadingElement>(null);
  const namesContainerRef = useRef<HTMLDivElement>(null);

  const handleRSVPSuccess = useCallback(() => {
    setRsvpSubmitted(true);
    showSuccess('Спасибо за ответ! Мы очень рады, что вы будете с нами!');
  }, [showSuccess]);

  const formattedDate = useMemo(
    () => (invite.weddingDate ? formatDateWithWeekday(invite.weddingDate) : ''),
    [invite.weddingDate],
  );

  const formattedLocation = useMemo(
    () => invite.weddingLocation?.replace(/^г\.\s*/i, '').replace(/\s*г\.\s*/gi, '') || '',
    [invite.weddingLocation],
  );

  // Адаптивная подгонка размера шрифта для имен
  useEffect(() => {
    const adjustFontSize = () => {
      const namesElement = namesRef.current;
      const containerElement = namesContainerRef.current;

      if (!namesElement || !containerElement) return;

      // Сбрасываем размер шрифта для корректного измерения
      namesElement.style.fontSize = '';

      const containerStyle = getComputedStyle(containerElement);
      const paddingLeft = parseFloat(containerStyle.paddingLeft);
      const paddingRight = parseFloat(containerStyle.paddingRight);
      const containerWidth = containerElement.offsetWidth - (paddingLeft + paddingRight) * 2;
      const containerHeight = containerElement.offsetHeight;
      let fontSize = parseFloat(getComputedStyle(namesElement).fontSize);
      const minFontSize = 32; // Минимальный размер 2rem (32px)

      // Проверяем, помещается ли текст
      const checkFit = () => {
        const textWidth = namesElement.scrollWidth;
        const textHeight = namesElement.scrollHeight;
        return textWidth <= containerWidth && textHeight <= containerHeight;
      };

      // Уменьшаем размер пока текст не поместится
      while (!checkFit() && fontSize > minFontSize) {
        fontSize -= 2;
        namesElement.style.fontSize = `${fontSize}px`;
      }
    };

    adjustFontSize();

    // Обработчик изменения размера окна
    const resizeObserver = new ResizeObserver(adjustFontSize);
    if (namesContainerRef.current) {
      resizeObserver.observe(namesContainerRef.current);
    }

    // Таймер для обработки изменений после загрузки
    const timeoutId = setTimeout(adjustFontSize, 100);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [invite.brideName, invite.groomName]);

  return (
    <div className={styles.container} data-theme="charming-natural">
      {/* Фиксированный header с навигацией */}
      <CharmingNaturalHeader
        brideName={invite.brideName}
        groomName={invite.groomName}
      />

      {/* Объединенная секция: имена + главная информация */}
      <section id="main" className={styles.unifiedSection}>
        {/* Секция с именами */}
        <div ref={namesContainerRef} className={styles.namesSection}>
          <motion.h1
            ref={namesRef}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className={styles.names}
          >
            {invite.brideName}
            {' '}
            &
            {' '}
            {invite.groomName}
          </motion.h1>
        </div>

        {/* Главная информация с фоном изображения */}
        <div className={styles.mainSection}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={styles.mainContent}
          >
            <motion.div
              id="details"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className={styles.info}
            >
              <p className={styles.invitationText}>
                С любовью и благодарностью мы приглашаем вас разделить радость нашего свадебного дня.
              </p>
              <div className={styles.infoItem}>
                <div className={styles.infoValue}>
                  {formattedDate}
                  <br />
                  {formattedLocation}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Навигационная витрина */}
      <CharmingNaturalNavigation invite={invite} />

      {/* Секция "Подтвердить присутствие" */}
      <section id="rsvp" className={styles.rsvpSection}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className={styles.rsvpContent}
        >
          {rsvpSubmitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className={styles.successMessage}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
                className={styles.checkmark}
              >
                ✓
              </motion.div>
              <h2 className={styles.successMessageTitle}>
                Спасибо за ответ!
              </h2>
              <p className={styles.successMessageText}>
                Мы очень рады, что вы будете с нами!
              </p>
            </motion.div>
          )}
          {!rsvpSubmitted && showRSVPForm && (
            <div className={styles.rsvpFormContainer}>
              <CharmingNaturalRSVPForm inviteId={invite.id} onSuccess={handleRSVPSuccess} />
            </div>
          )}
          {!rsvpSubmitted && !showRSVPForm && (
            <div className={styles.rsvpInvitation}>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={styles.rsvpInvitationText}
              >
                We invite you to our wedding at Golden Elm Manor, an enchanting
                place where towering elms meet candlelit pathways. And where our
                next adventure begins.
              </motion.p>
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}
                onClick={() => setShowRSVPForm(true)}
                className={styles.rsvpButton}
                type="button"
              >
                RSVP
              </motion.button>
            </div>
          )}
        </motion.div>
      </section>

      {/* Footer с логотипом и датой свадьбы */}
      <CharmingNaturalFooter
        brideName={invite.brideName}
        groomName={invite.groomName}
        weddingDate={invite.weddingDate}
      />
    </div>
  );
});

CharmingNaturalLayout.displayName = 'CharmingNaturalLayout';

export default CharmingNaturalLayout;
